<template>
    <modal ref="modal" :has-footer="false" :size="modalSize">
        <div class="d-block text-center" slot="title">
            Send Bulk Invites
        </div>
        <div class="animated animated-normal fadeIn" :key="key">
            <div v-if="!showErrorsTable">
                <div class="form-group">
                    <label>Location</label>
                    <multiselect
                        :class="{'is-invalid': form.errors.has('location_id')}"
                        id="location_id"
                        :options="locations"
                        placeholder="Select a location"
                        track-by="id"
                        label="name"
                        selected="location_id"
                        v-model="location_id"
                        @select="locationSelected"
                        @remove="deselectLocation"
                    />
                    <p class="invalid-feedback" v-if="form.errors.has('location_id')">
                        {{ form.errors.get('location_id') }}
                    </p>
                </div>

                <div class="form-group">
                    <label>Campaign</label>
                    <multiselect
                        :class="{'is-invalid': form.errors.has('campaign_id')}"
                        id="campaign_id"
                        v-model="campaign_id"
                        :options="campaigns"
                        placeholder="Select a campaign"
                        track-by="id"
                        label="name"
                        :loading="loading_campaigns"
                        @select="selectModel"
                        @remove="deselectModel"
                    />
                    <p class="invalid-feedback" v-if="form.errors.has('campaign_id')">
                        {{ form.errors.get('campaign_id') }}
                    </p>
                </div>

                <div class="form-group">
                    <label class="mb-0">
                        <input type="checkbox" @click="sendLater = !sendLater"/>
                        Send later
                    </label>
                </div>

                <div class="form-group" v-if="sendLater">
                    <label>Select a date and time</label>
                    <small v-if="!timezone" class="ml-auto float-right text-muted" style="padding-top: 4px">
                        Server Time:
                        <server-time
                            class="ml-1"
                            format="hh:mm:ss a"
                        />
                    </small>
                    <small v-else class="ml-auto float-right text-muted" style="padding-top: 4px">
                        Your Time:
                        <server-time
                            class="ml-1"
                            format="hh:mm:ss a"
                            :timezone="timezone"
                        />
                    </small>
                    <div class="input-group">
                        <flat-pickr
                            class="form-control"
                            :class="{'is-invalid': form.errors.has('send_at')}"
                            v-model="form.send_at"
                            :config="config"
                            placeholder="Select date"
                            name="date"
                        />
                        <div class="input-group-append">
                            <button class="btn btn-default" type="button" title="Clear" data-clear=""
                                @click="form.send_at = null">
                                <i class="fa fa-times"><span class="sr-only" aria-hidden="true">Clear</span></i>
                            </button>
                        </div>
                        <p class="invalid-feedback" v-if="form.errors.has('send_at')">
                            {{ form.errors.get('send_at') }}
                        </p>
                    </div>
                </div>

                <div class="form-row">
                    <div class="col text-right">
                        <button-loading
                            class="btn btn-primary px-4"
                            type="button"
                            :loading="form.submitting"
                            data-dismiss="modal"
                            @click="sendNow">
                            {{ !sendLater ? 'Send' : 'Schedule'}}
                        </button-loading>
                    </div>
                </div>
            </div>

            <div class="alert alert-warning animated pulse" v-if="showErrorsTable">
                <b>Unable to send invite to the following contacts:</b>
            </div>
            <v-client-table
                v-if="showErrorsTable"
                ref="table"
                :data="bulkInviteErrors"
                :columns="columns"
                :options="options"
            >
                <template slot="beforeTable">
                    <div class="form-group text-right">
                        <label for="select_all" class="mb-0">Mark all:</label>
                        <input type="checkbox" name="select_all" id="select_all"
                            v-model="markAllOverride"
                            @change="markAll()">
                    </div>
                </template>
                <div class="text-center" slot="id" slot-scope="{row}">
                    <input type="checkbox" v-model="row.override" @change="markOverride(row.id)">
                </div>
            </v-client-table>

            <div class="text-right" v-if="showErrorsTable">
                <button type="button" class="btn btn-secondary mr-1" data-dismiss="modal" @click="inviteProcessComplete">
                    Close
                </button>
                <button-loading
                    class="btn btn-primary px-4 ml-1"
                    type="button"
                    :loading="form.submitting"
                    data-dismiss="modal"
                    @click="sendOverride"
                    :disabled="!enableOverride">
                    Override
                </button-loading>
            </div>
        </div>
    </modal>
</template>

<script>
    import flatPickr      from 'vue-flatpickr-component';
    import 'flatpickr/dist/flatpickr.css';
    import {mapState}     from 'vuex';
    import ModalComponent from '../../mixins/ModalComponent';

    export default {
        mixins: [ModalComponent],
        props : {
            contacts : {
                type    : Array,
                default : [],
                required: true,
            },
            isContact: {default: true},
            preselectedLocation: {default: null}
        },

        data() {
            return {
                timezone         : null,
                columns          : ['contact', 'reason', 'id'],
                options          : {
                    headings: {
                        contact: 'Contact',
                        reason : 'Reason',
                        id     : 'Override'
                    },
                    sortable: ['contact', 'reason'],
                    filterable: ['contact']
                },
                key              : null,
                location_id      : 1,
                locations        : [],
                fetched_locations: [],
                campaigns        : [],
                campaign_id      : null,
                form             : new Form(),
                sendLater        : false,
                loading_campaigns: false,
                config           : {
                    wrap      : true, // set wrap to true only when using 'input-group'
                    altFormat : 'M j, Y H:i',
                    altInput  : true,
                    enableTime: true,
                    dateFormat: "Y-m-d H:i",
                    minDate   : "today",
                },
                showErrorsTable  : false,
                bulkInviteErrors : [],
                markAllOverride  : false,
                locationFocus    : null
            };
        },

        components: {
            flatPickr
        },

        created() {
            this.fetchTimezone();
            this.setupForm();
            this.sendLater = false;
            this.fetchLocations();
        },

        computed: {
            ...mapState({
                filter: state => state.filter,
            }),

            modalSize() {
                return this.showErrorsTable ? 'lg' : 'md';
            },

            enableOverride() {
                return this.form.contacts.length;
            },

            hasOneLocation() {
                return this.locations.length === 1;
            },

            hasOnecampaign() {
                return this.campaigns.length === 1;
            }
        },

        watch: {
            locations() {
                if (this.hasOneLocation) {
                    this.location_id = this.locations[0];
                    this.locationSelected(this.locations[0]);
                }
            },

            campaigns() {
                if (this.hasOnecampaign) {
                    this.campaign_id = this.campaigns[0];
                    this.selectModel(this.campaign_id, 'campaign_id');
                }
            },
            preselectedLocation: {
                immediate: true,
                handler() {
                    if(!!this.preselectedLocation){
                        this.locations = [this.preselectedLocation];
                    }else{
                        this.locations = this.fetched_locations;
                    }
                }
            }
        },

        methods: {
            fetchTimezone()  {
                return this.$http.get(this.$laroute.route('api.teams.current-team-timezone')).then(({ data }) => {
                    this.timezone = data;
                });
            },
            afterOpen() {
                this.showErrorsTable = false;
                this.setupForm();
                this.key              = Math.random().toString(36);
                this.bulkInviteErrors = [];

                if (!this.hasOneLocation) {
                    this.location_id = null;
                    this.campaign_id = null;
                }
            },

            setupForm() {
                this.form = new Form({
                    location_id: this.hasOneLocation ? this.locations[0].id : null,
                    campaign_id: this.hasOnecampaign ? this.campaigns[0].id : null,
                    contacts   : this.contacts,
                    is_bulk    : true,
                    isContact  : this.isContact,
                    timezone   : this.timezone,
                });
            },

            fetchLocations() {
                return this.$http.get(this.$laroute.route('api.global-filters.location', {'hide': 'campaigns'}))
                    .then(({data}) => {
                        this.locations = data.data;
                        this.fetched_locations = data.data;
                    });
            },

            fetchCampaigns(location_id) {
                this.loading_campaigns = true;
                this.form.campaign_id  = null;
                this.campaigns         = [];
                this.campaign_id       = null;

                return this.$http.get(this.$laroute.route('api.locations.campaigns', {location: location_id}))
                    .then(({data}) => {
                        this.campaigns         = data.data;
                        this.loading_campaigns = false;
                    });
            },

            locationSelected(location) {
                this.form.location_id = location.id;
                this.fetchCampaigns(location.id);
            },

            deselectLocation() {
                this.form.location_id = null;
                this.form.campaign_id = null;
                this.campaign_id      = null;
            },

            selectModel(model, option) {
                this.form[option] = model.id;
            },

            deselectModel(model, option) {
                this.form[option] = undefined;
            },
            inviteProcessComplete()
            {
                //this.locations = this.fetched_locations;
                this.$emit('inviteProcessComplete');
            },
            sendNow() {
                this.form.scheduled = this.sendLater;
                this.form.post(this.$laroute.route('api.invites.store-bulk'))
                    .then(response => {
                        if (!response.data.errors.length) {
                            this.$alert({type: 'success', title: 'Invite(s) sent!'});
                            this.$refs.modal.close();
                            this.inviteProcessComplete();
                        }

                        if (response.data.errors.length) {
                            this.form.contacts = [];
                            this.bulkInviteErrors = [];
                            this.handleBulkInviteErrors(response.data.errors)
                        }

                        this.$emit('unselectAll');
                    })
                    .catch(({data}) => {
                        swal.fire({
                            title: data.title,
                            text : data.message,
                            type : 'error',
                        })
                    });
            },

            sendOverride() {
                this.form.isContact = true;
                this.form.override  = true;
                return this.sendNow();
            },

            handleBulkInviteErrors(errors) {
                errors.map(err => {
                    let contactId   = err.id;
                    let contactName = err.contact;
                    err.error.contact.map(msg => {
                        let obj = {
                            'id'     : contactId,
                            'contact': contactName,
                            'reason' : msg,
                            'override': false,
                        };
                        this.bulkInviteErrors.push(obj);
                    });
                });
                this.showErrorsTable = true;
            },

            markAll() {
                let override = false;

                if (this.markAllOverride) {
                    override = true;
                }

                this.bulkInviteErrors.map(contact => contact.override = override);

                this.mapContactsToOverride();
            },

            markOverride(contact_id) {
                this.markAllOverride = false;
                let contact = this.bulkInviteErrors.find(elem => elem.id === contact_id);
                contact.override = !contact.override;

                this.mapContactsToOverride();
            },

            mapContactsToOverride() {
                this.form.contacts = this.bulkInviteErrors.filter(contact => contact.override === true)
                    .map(contact => contact.id);
            }
        }
    }
</script>
